import {
  Control,
  Controller,
  FieldErrors,
  UseFormWatch,
} from 'react-hook-form';
import { useEffect, useState } from 'react';
import { User } from '../../../../../types';
import { UserFormData } from '../../add-user-dialog/user-form-page';

import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import ExpandMore from '@mui/icons-material/ExpandMore';

interface GenderAccordionProps {
  control: Control<UserFormData>;
  errors: FieldErrors<UserFormData>;
  watch: UseFormWatch<UserFormData>;
  user: User | null;
  disabled: boolean;
}

export default function GenderAccordion({
  control,
  errors,
  disabled,
  watch,
  user,
}: GenderAccordionProps) {
  const [open, setOpen] = useState(false);

  const genderErrors = errors['gender'];

  // close accordion if there are no items in the accordion
  useEffect(() => {
    if (
      user?.gender === null ||
      user?.gender === undefined ||
      user.gender?.type === ''
    ) {
      setOpen(false);
    } else {
      setOpen(true);
    }
  }, [user?.gender]);

  // open accordion if there is an error in the accordion
  useEffect(() => {
    if (genderErrors) {
      setOpen(true);
    }
  }, [genderErrors]);

  return (
    <Accordion
      expanded={open}
      onChange={() => {
        setOpen(!open);
      }}
    >
      <AccordionSummary expandIcon={<ExpandMore />}>
        <Stack flexDirection="row" alignItems="center" gap={1}>
          <Typography
            sx={{
              color: (theme) =>
                genderErrors ? theme.palette.error.main : 'inherit',
            }}
          >
            Gender
          </Typography>
        </Stack>
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 2 }}>
        <Stack
          gap={1}
          flexDirection="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ my: 1.5 }}
        >
          <Stack flexDirection="row" alignItems="center" gap={2}>
            <Controller
              name="gender.type"
              control={control}
              rules={{
                validate: (value) => {
                  if (watch('gender.addressMeAs') !== '' && !value) {
                    return 'Gender must be selected if "Address Me As" is filled in.';
                  }
                  return true;
                },
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  inputProps={{
                    id: 'gender',
                  }}
                  InputLabelProps={{
                    htmlFor: 'gender',
                  }}
                  name="gender"
                  select
                  label="Gender"
                  sx={{ minWidth: 200 }}
                  error={!!genderErrors?.type}
                  disabled={disabled}
                  helperText={
                    genderErrors?.type
                      ? 'Gender must be selected if "Address Me As" is filled in.'
                      : ''
                  }
                >
                  <MenuItem value={'female'}>Female</MenuItem>
                  <MenuItem value={'male'}>Male</MenuItem>
                  <MenuItem value={'other'}>Other</MenuItem>
                  <MenuItem value={'unknown'}>Unknown</MenuItem>
                </TextField>
              )}
            />
            <Controller
              name="gender.addressMeAs"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  autoComplete="off"
                  label="Address Me As"
                  sx={{ minWidth: 400 }}
                  disabled={disabled}
                />
              )}
            />
          </Stack>
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
}
